import React from "react";
import {
  Collapse,
  Divider,
  List,
  Progress,
  Space,
  Tag,
  Typography,
} from "antd";
import { ExperimentOutlined } from "@ant-design/icons";

const { Text, Paragraph } = Typography;

const scoreColor = (score) => {
  const n = Number(score || 0);
  if (n >= 8) return "#52c41a";
  if (n >= 5) return "#faad14";
  return "#ff4d4f";
};

const gradeColor = (grade) => {
  const g = String(grade || "").toUpperCase();
  if (g === "A") return "#52c41a";
  if (g === "B") return "#1677ff";
  if (g === "C") return "#faad14";
  return "#ff4d4f";
};

const activationColor = (level) => {
  const l = String(level || "").toLowerCase();
  if (l === "high") return "red";
  if (l === "medium") return "orange";
  return "green";
};

function MetricBar({ label, value, max = 100, suffix = "%", color }) {
  const n = Number(value || 0);
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <Text style={{ fontSize: 11 }}>{label}</Text>
        <Text strong style={{ fontSize: 11, color: color || scoreColor(n / 10) }}>
          {n}{suffix}
        </Text>
      </div>
      <Progress
        percent={Math.min(100, (n / max) * 100)}
        showInfo={false}
        strokeColor={color || scoreColor(n / 10)}
        size="small"
        style={{ marginBottom: 0 }}
      />
    </div>
  );
}

function BrainRegionBadge({ region, activation, role }) {
  return (
    <Tag
      color={activationColor(activation)}
      style={{ marginBottom: 4, fontSize: 11 }}
    >
      <strong>{region}</strong>
      {role ? ` — ${role}` : ""}
    </Tag>
  );
}

export default function NeuroReport({ report, error }) {
  if (error) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Neuro report failed: {error}
      </Text>
    );
  }

  if (!report) return null;

  const scorecard = (
    <div
      style={{
        background: "linear-gradient(135deg, rgba(22,119,255,0.06) 0%, rgba(114,46,209,0.06) 100%)",
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        marginBottom: 8,
      }}
    >
      <div style={{ textAlign: "center", minWidth: 70 }}>
        <div
          style={{
            fontSize: 36,
            fontWeight: 800,
            color: gradeColor(report.grade),
            lineHeight: 1,
          }}
        >
          {report.grade}
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: scoreColor(report.overallScore / 10),
          }}
        >
          {report.overallScore}/100
        </div>
        <Text type="secondary" style={{ fontSize: 10 }}>
          Neuro Score
        </Text>
      </div>
      <Paragraph style={{ marginBottom: 0, flex: 1, fontSize: 12, lineHeight: "1.5" }}>
        {report.executiveSummary || report.reportTitle || "Analysis complete."}
      </Paragraph>
    </div>
  );

  const predictions = report.predictedPerformance || {};
  const predictionSection = (
    <div style={{ marginBottom: 8 }}>
      {predictions.hookRate?.value != null && (
        <MetricBar
          label="Hook Rate"
          value={predictions.hookRate.value}
          suffix="%"
          max={100}
        />
      )}
      {predictions.holdRate?.value != null && (
        <MetricBar
          label="Hold Rate"
          value={predictions.holdRate.value}
          suffix="%"
          max={100}
        />
      )}
      {predictions.completionRate?.value != null && (
        <MetricBar
          label="Completion Rate"
          value={predictions.completionRate.value}
          suffix="%"
          max={100}
        />
      )}
      {predictions.clickIntent?.value != null && (
        <MetricBar
          label="Click Intent"
          value={predictions.clickIntent.value}
          suffix="%"
          max={100}
        />
      )}
    </div>
  );

  const brainTimeline =
    report.brainScanTimeline?.length > 0 ? (
      <div>
        {report.brainScanTimeline.map((entry, i) => (
          <div
            key={i}
            style={{
              padding: "8px 10px",
              borderRadius: 6,
              border: "1px solid rgba(148, 163, 184, 0.15)",
              marginBottom: 6,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <Text strong style={{ fontSize: 12 }}>
                Segment {entry.segment} ({entry.timeRange})
              </Text>
              <Space size={4}>
                <Tag color={scoreColor(entry.attentionLevel)} style={{ fontSize: 10, margin: 0 }}>
                  ATT {entry.attentionLevel}
                </Tag>
                <Tag color={scoreColor(entry.emotionalValence)} style={{ fontSize: 10, margin: 0 }}>
                  EMO {entry.emotionalValence}
                </Tag>
                <Tag
                  color={entry.cognitiveLoad >= 8 ? "red" : entry.cognitiveLoad >= 5 ? "orange" : "green"}
                  style={{ fontSize: 10, margin: 0 }}
                >
                  COG {entry.cognitiveLoad}
                </Tag>
              </Space>
            </div>
            <Paragraph style={{ fontSize: 11, marginBottom: 4, lineHeight: "1.4" }}>
              {entry.neuralNarrative}
            </Paragraph>
            <Space size={[4, 4]} wrap>
              {(entry.dominantBrainRegions || []).map((r, j) => (
                <BrainRegionBadge
                  key={j}
                  region={r.region || r.name}
                  activation={r.activation || r.activation_level}
                  role={r.role || r.function}
                />
              ))}
            </Space>
            {entry.stitchContinuityScore > 0 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Stitch continuity: {entry.stitchContinuityScore}/10
                </Text>
              </div>
            )}
          </div>
        ))}
      </div>
    ) : null;

  const emotionalArc = report.emotionalArc ? (
    <div style={{ marginBottom: 8 }}>
      <Space size={[6, 6]} wrap style={{ marginBottom: 6 }}>
        {report.emotionalArc.openingEmotion && (
          <Tag color="blue">{report.emotionalArc.openingEmotion}</Tag>
        )}
        <Text type="secondary" style={{ fontSize: 11 }}>
          &rarr;
        </Text>
        {report.emotionalArc.peakEmotion && (
          <Tag color="purple">{report.emotionalArc.peakEmotion}</Tag>
        )}
        <Text type="secondary" style={{ fontSize: 11 }}>
          &rarr;
        </Text>
        {report.emotionalArc.closingEmotion && (
          <Tag color="cyan">{report.emotionalArc.closingEmotion}</Tag>
        )}
      </Space>
      {report.emotionalArc.arcType && (
        <Paragraph style={{ fontSize: 11, marginBottom: 4 }}>
          <Text strong style={{ fontSize: 11 }}>Arc: </Text>
          {report.emotionalArc.arcType}
        </Paragraph>
      )}
      <Space size={[4, 4]} wrap>
        {(report.emotionalArc.dopamineTriggers || []).map((t, i) => (
          <Tag key={`d-${i}`} color="green" style={{ fontSize: 10 }}>
            Dopamine: {t}
          </Tag>
        ))}
        {(report.emotionalArc.cortisolTriggers || []).map((t, i) => (
          <Tag key={`c-${i}`} color="red" style={{ fontSize: 10 }}>
            Cortisol: {t}
          </Tag>
        ))}
        {(report.emotionalArc.oxytocinTriggers || []).map((t, i) => (
          <Tag key={`o-${i}`} color="pink" style={{ fontSize: 10 }}>
            Oxytocin: {t}
          </Tag>
        ))}
      </Space>
    </div>
  ) : null;

  const attentionCurve = report.attentionCurve ? (
    <div style={{ marginBottom: 8 }}>
      <Paragraph style={{ fontSize: 11, marginBottom: 4 }}>
        {report.attentionCurve.flowDescription}
      </Paragraph>
      <Space size={[6, 6]} wrap>
        {report.attentionCurve.peakAttentionTimestamp && (
          <Tag color="green" style={{ fontSize: 10 }}>
            Peak: {report.attentionCurve.peakAttentionTimestamp}
          </Tag>
        )}
        {report.attentionCurve.attentionDropTimestamp && (
          <Tag color="red" style={{ fontSize: 10 }}>
            Drop: {report.attentionCurve.attentionDropTimestamp}
          </Tag>
        )}
        {report.attentionCurve.predictedDropOffPoint && (
          <Tag color="orange" style={{ fontSize: 10 }}>
            Drop-off: {report.attentionCurve.predictedDropOffPoint}
          </Tag>
        )}
      </Space>
    </div>
  ) : null;

  const memorySection = report.memoryEncoding ? (
    <div style={{ marginBottom: 8 }}>
      <MetricBar
        label="Memory Encoding"
        value={report.memoryEncoding.score}
        max={10}
        suffix="/10"
      />
      {report.memoryEncoding.brandRecallProbability && (
        <Paragraph style={{ fontSize: 11, marginBottom: 2 }}>
          <Text strong style={{ fontSize: 11 }}>Brand Recall: </Text>
          {report.memoryEncoding.brandRecallProbability}
        </Paragraph>
      )}
      {report.memoryEncoding.strongestMemoryAnchor && (
        <Paragraph style={{ fontSize: 11, marginBottom: 2 }}>
          <Text strong style={{ fontSize: 11 }}>Strongest Anchor: </Text>
          {report.memoryEncoding.strongestMemoryAnchor}
        </Paragraph>
      )}
      {report.memoryEncoding.peakEndRuleCompliance && (
        <Paragraph style={{ fontSize: 11, marginBottom: 2 }}>
          <Text strong style={{ fontSize: 11 }}>Peak-End Rule: </Text>
          {report.memoryEncoding.peakEndRuleCompliance}
        </Paragraph>
      )}
    </div>
  ) : null;

  const stitchSection =
    report.stitchAnalysis?.length > 0 ? (
      <List
        size="small"
        bordered
        dataSource={report.stitchAnalysis}
        renderItem={(item) => (
          <List.Item>
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              <Space>
                <Text strong style={{ fontSize: 11 }}>
                  {item.transition || item.label || "Transition"}
                </Text>
                <Tag
                  color={
                    Number(item.continuity_score || item.continuityScore || 0) >= 7
                      ? "green"
                      : Number(item.continuity_score || item.continuityScore || 0) >= 4
                        ? "orange"
                        : "red"
                  }
                  style={{ fontSize: 10 }}
                >
                  {item.continuity_score || item.continuityScore || "?"}/10
                </Tag>
              </Space>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {item.cognitive_bridging || item.cognitiveBridging || ""}
              </Text>
              {(item.suggestion || item.neural_disruption_risk || item.neuralDisruptionRisk) && (
                <Text style={{ fontSize: 11, color: "#1677ff" }}>
                  {item.suggestion || `Risk: ${item.neural_disruption_risk || item.neuralDisruptionRisk}`}
                </Text>
              )}
            </Space>
          </List.Item>
        )}
      />
    ) : null;

  const brainMap =
    report.brainRegionSummary?.length > 0 ? (
      <Space size={[4, 4]} wrap>
        {report.brainRegionSummary.map((r, i) => (
          <Tag
            key={i}
            color={activationColor(r.overall_activation || r.overallActivation)}
            style={{ fontSize: 10, marginBottom: 2 }}
          >
            <strong>{r.region}</strong>
            {r.function ? ` (${r.function})` : ""}
            {r.peak_activation_segment || r.peakActivationSegment
              ? ` — peak seg ${r.peak_activation_segment || r.peakActivationSegment}`
              : ""}
          </Tag>
        ))}
      </Space>
    ) : null;

  const improvements =
    report.improvementRoadmap?.length > 0 ? (
      <List
        size="small"
        bordered
        dataSource={report.improvementRoadmap}
        renderItem={(item, index) => (
          <List.Item>
            <Space direction="vertical" size={2} style={{ width: "100%" }}>
              <Space>
                <Tag color="blue" style={{ fontSize: 10 }}>
                  #{index + 1}
                </Tag>
                <Text strong style={{ fontSize: 11 }}>
                  {item.area || "Improvement"}
                </Text>
                {(item.segment_affected || item.segmentAffected) ? (
                  <Tag style={{ fontSize: 10 }}>
                    Seg {item.segment_affected || item.segmentAffected}
                  </Tag>
                ) : null}
                {(item.effort) && (
                  <Tag
                    color={item.effort === "low" ? "green" : item.effort === "medium" ? "orange" : "red"}
                    style={{ fontSize: 10 }}
                  >
                    {item.effort} effort
                  </Tag>
                )}
              </Space>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {item.current_state || item.currentState || ""}
              </Text>
              <Text style={{ fontSize: 11 }}>
                <Text strong style={{ fontSize: 11 }}>Why: </Text>
                {item.neuroscience_rationale || item.neuroscienceRationale || ""}
              </Text>
              <Text style={{ fontSize: 11, color: "#1677ff" }}>
                <Text strong style={{ fontSize: 11, color: "#1677ff" }}>Change: </Text>
                {item.specific_change || item.specificChange || ""}
              </Text>
              {Number(item.predicted_score_lift || item.predictedScoreLift || 0) > 0 && (
                <Text style={{ fontSize: 11, color: "#52c41a" }}>
                  Predicted lift: +{item.predicted_score_lift || item.predictedScoreLift} points
                </Text>
              )}
            </Space>
          </List.Item>
        )}
      />
    ) : null;

  return (
    <Collapse
      size="small"
      style={{ marginBottom: 8 }}
      items={[
        {
          key: "neuro",
          label: (
            <Space size={6}>
              <ExperimentOutlined />
              <Text strong style={{ fontSize: 12 }}>
                Neuro Report
              </Text>
              <Tag
                color={gradeColor(report.grade)}
                style={{ fontSize: 11, fontWeight: 700, margin: 0 }}
              >
                {report.grade} ({report.overallScore}/100)
              </Tag>
            </Space>
          ),
          children: (
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {scorecard}

              <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                Predicted Performance
              </Divider>
              {predictionSection}

              {brainTimeline && (
                <>
                  <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                    <ExperimentOutlined /> Brain Scan Timeline
                  </Divider>
                  {brainTimeline}
                </>
              )}

              {attentionCurve && (
                <>
                  <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                    Attention Curve
                  </Divider>
                  {attentionCurve}
                </>
              )}

              {emotionalArc && (
                <>
                  <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                    Emotional Arc
                  </Divider>
                  {emotionalArc}
                </>
              )}

              {memorySection && (
                <>
                  <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                    Memory Encoding
                  </Divider>
                  {memorySection}
                </>
              )}

              {stitchSection && (
                <>
                  <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                    Stitch Transitions
                  </Divider>
                  {stitchSection}
                </>
              )}

              {brainMap && (
                <>
                  <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                    Brain Region Map
                  </Divider>
                  {brainMap}
                </>
              )}

              {improvements && (
                <>
                  <Divider orientation="left" style={{ margin: "6px 0", fontSize: 12 }}>
                    Improvement Roadmap
                  </Divider>
                  {improvements}
                </>
              )}
            </Space>
          ),
        },
      ]}
    />
  );
}
